/**
 * Page structure.
 *
 *   <Page>
 *     <PageHeader breadcrumb title subtitle meta actions />
 *     <StatGrid> <StatCard/>… </StatGrid>          ← figures sit on the page
 *     <Grid min="22rem"> <Card/> <ChartCard/> </Grid>
 *     <Card title actions footer> <DataTable/> </Card>
 *   </Page>
 *
 * Cards never nest. Something that needs a sub-surface inside a card uses a
 * `Divider` or a `DescriptionList`, not a second border.
 */
import { CaretLeft } from '@phosphor-icons/react';
import { useId, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cx, ui } from './text';

/** Steps on the spacing scale, written out so every token a component reads is greppable. */
const SPACE = {
  '0': '0',
  '1': 'var(--space-1)',
  '2': 'var(--space-2)',
  '3': 'var(--space-3)',
  '4': 'var(--space-4)',
  '5': 'var(--space-5)',
  '6': 'var(--space-6)',
  '8': 'var(--space-8)',
  '10': 'var(--space-10)',
  '12': 'var(--space-12)',
} as const;

type Space = keyof typeof SPACE;

/* ── Page ─────────────────────────────────────────────────────────── */

export interface PageProps {
  children: ReactNode;
  /**
   * How much of a wide screen the page takes.
   *
   * - `default` — the full width. For lists, tables, charts and dashboards:
   *   what is scanned across uses the screen. Fields and running text inside
   *   still cap themselves (`--form-max`, `--measure`), so a form or a notice
   *   on this page does not stretch to 2560px.
   * - `detail` — at most `--detail-max` (1440px), centred. A record read top
   *   to bottom (settings, a description list, a form beside a summary).
   * - `form` — at most `--form-max` (760px). The page *is* a form (a wizard).
   * - `wide` — the same as `default` now that the panel is full width. Kept so
   *   existing pages compile; prefer `default`.
   */
  width?: 'default' | 'detail' | 'form' | 'wide';
}

export function Page({ children, width = 'default' }: PageProps) {
  return (
    <div className="page" data-width={width}>
      {children}
    </div>
  );
}

export interface Crumb {
  label: string;
  /** Omit for the current page. */
  to?: string;
}

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Ancestors, root first. The current page is the title — do not repeat it here. */
  breadcrumb?: Crumb[];
  /** Chips and identifiers under the title: status, slug, vertical. */
  meta?: ReactNode;
  /** One primary action at most, then secondary ones. Rendered at the inline end. */
  actions?: ReactNode;
  /** Leading visual — a logo or avatar. */
  media?: ReactNode;
}

export function PageHeader({ title, subtitle, breadcrumb, meta, actions, media }: PageHeaderProps) {
  return (
    <header className="page-header">
      {breadcrumb === undefined || breadcrumb.length === 0 ? null : (
        <nav className="breadcrumb" aria-label={ui('breadcrumb')}>
          <ol>
            {breadcrumb.map((crumb, index) => (
              <li key={`${crumb.label}-${index}`}>
                {crumb.to === undefined ? <span>{crumb.label}</span> : <Link to={crumb.to}>{crumb.label}</Link>}
                {/* CaretLeft points "forward" in RTL. */}
                <CaretLeft className="breadcrumb-sep" size={12} aria-hidden="true" />
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="page-header-row">
        {media === undefined ? null : <div className="page-header-media">{media}</div>}
        <div className="page-header-text">
          <h1 className="page-title">{title}</h1>
          {subtitle === undefined ? null : <p className="page-subtitle">{subtitle}</p>}
          {meta === undefined ? null : <div className="page-meta">{meta}</div>}
        </div>
        {actions === undefined ? null : <div className="page-actions">{actions}</div>}
      </div>
    </header>
  );
}

/* ── Section ──────────────────────────────────────────────────────── */

export interface SectionProps {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** For `aria-labelledby` and in-page links. */
  id?: string;
}

/** A titled group on the page, without a surface of its own. */
export function Section({ title, description, actions, children, id }: SectionProps) {
  const headingId = id === undefined ? undefined : `${id}-title`;

  return (
    <section className="section" id={id} aria-labelledby={headingId}>
      <div className="section-head">
        <div>
          <h2 className="section-title" id={headingId}>
            {title}
          </h2>
          {description === undefined ? null : <p className="section-description">{description}</p>}
        </div>
        {actions === undefined ? null : <div className="section-actions">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

export interface SubsectionProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** For `aria-labelledby` and in-page links. Generated when omitted. */
  id?: string;
}

/**
 * A titled block one level below a card or drawer title — the drawer's
 * «البيانات الوصفية» and «المعرّفات». An `h3`, so the outline stays
 * page (h1) → card or drawer (h2) → this.
 */
export function Subsection({ title, description, actions, children, id }: SubsectionProps) {
  const generated = useId();
  const headingId = `${id ?? generated}-title`;

  return (
    <section className="subsection" id={id} aria-labelledby={headingId}>
      <div className="subsection-head">
        <div>
          <h3 className="subsection-title" id={headingId}>
            {title}
          </h3>
          {description === undefined ? null : <p className="subsection-description">{description}</p>}
        </div>
        {actions === undefined ? null : <div className="section-actions">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/* ── Card ─────────────────────────────────────────────────────────── */

export interface CardProps {
  title?: ReactNode;
  description?: ReactNode;
  /** Filters, a secondary action, a count — at the inline end of the head. */
  actions?: ReactNode;
  /** Pagination, a "view all" link, a footnote. */
  footer?: ReactNode;
  /** `none` for a card whose child (a table, a list) draws its own padding. */
  padding?: 'none' | 'md' | 'lg';
  /** Rendered as the heading level; defaults to h2. */
  headingLevel?: 2 | 3;
  children: ReactNode;
  className?: string;
  /** Marks the region busy while it loads. */
  busy?: boolean;
  /**
   * For a deep link (`…/mail#mail-test`) and for `aria-labelledby`: the card
   * becomes a region named by its title, and lands clear of the toolbar and
   * context bar when scrolled to.
   */
  id?: string;
}

export function Card({ title, description, actions, footer, padding = 'md', headingLevel = 2, children, className, busy = false, id }: CardProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const hasHead = title !== undefined || actions !== undefined;
  const headingId = id === undefined || title === undefined ? undefined : `${id}-title`;

  return (
    <section className={cx('card', className)} id={id} aria-labelledby={headingId} aria-busy={busy || undefined}>
      {hasHead ? (
        <div className="card-head">
          <div className="card-head-text">
            {title === undefined ? null : (
              <Heading className="card-title" id={headingId}>
                {title}
              </Heading>
            )}
            {description === undefined ? null : <p className="card-description">{description}</p>}
          </div>
          {actions === undefined ? null : <div className="card-actions">{actions}</div>}
        </div>
      ) : null}
      <div className="card-body" data-padding={padding}>
        {children}
      </div>
      {footer === undefined ? null : <div className="card-foot">{footer}</div>}
    </section>
  );
}

/* ── Layout helpers ───────────────────────────────────────────────── */

export interface StackProps {
  gap?: Space;
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'ul' | 'ol' | 'form';
}

/** Vertical rhythm on the spacing scale. */
export function Stack({ gap = '4', children, className, as: Element = 'div' }: StackProps) {
  return (
    <Element className={cx('stack', className)} style={{ gap: SPACE[gap] }}>
      {children}
    </Element>
  );
}

export interface InlineProps {
  gap?: Space;
  /** `between` pushes the last child to the inline end. */
  justify?: 'start' | 'end' | 'between' | 'center';
  align?: 'start' | 'center' | 'end' | 'baseline';
  /** Defaults to wrapping, so long Arabic labels never clip. */
  wrap?: boolean;
  children: ReactNode;
  className?: string;
}

export function Inline({ gap = '2', justify = 'start', align = 'center', wrap = true, children, className }: InlineProps) {
  const style: CSSProperties = {
    gap: SPACE[gap],
    justifyContent: justify === 'between' ? 'space-between' : justify === 'start' ? 'flex-start' : justify === 'end' ? 'flex-end' : 'center',
    alignItems: align === 'start' ? 'flex-start' : align === 'end' ? 'flex-end' : align,
    flexWrap: wrap ? 'wrap' : 'nowrap',
  };

  return (
    <div className={cx('inline', className)} style={style}>
      {children}
    </div>
  );
}

export interface GridProps {
  /** Minimum column width; columns are as many as fit. */
  min?: string;
  gap?: Space;
  /** Fixed column count on wide screens (collapses to one column below 900px). */
  columns?: 2 | 3 | 4;
  children: ReactNode;
  className?: string;
}

export function Grid({ min = '18rem', gap = '4', columns, children, className }: GridProps) {
  return (
    <div
      className={cx('grid', className)}
      data-columns={columns}
      style={{ gap: SPACE[gap], '--grid-min': min } as CSSProperties}
    >
      {children}
    </div>
  );
}

export function Divider() {
  return <hr className="divider" />;
}

/* ── DescriptionList ──────────────────────────────────────────────── */

export interface Description {
  label: string;
  value: ReactNode;
  /** Shown under the value in muted text. */
  hint?: ReactNode;
}

/**
 * Label / value pairs: an identity block, configuration at a glance.
 * `columns` is a ceiling — at most that many, fewer when the box is narrow —
 * so a "two-column" list stays two columns in a full-width card at 2560px.
 * A long value wraps; an identifier in `Mono` wraps between its parts.
 */
export function DescriptionList({ items, columns = 2 }: { items: Description[]; columns?: 1 | 2 | 3 }) {
  return (
    <dl className="dl" data-columns={columns}>
      {items.map((item) => (
        <div className="dl-item" key={item.label}>
          <dt>{item.label}</dt>
          <dd>
            {item.value}
            {item.hint === undefined ? null : <span className="dl-hint">{item.hint}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/* ── FilterBar ────────────────────────────────────────────────────── */

export interface FilterBarProps {
  children: ReactNode;
  /** A count, a reset button, a segmented filter — at the inline end. */
  end?: ReactNode;
  /**
   * The filters are labelled fields (`FilterField` around a `TextField` or
   * `SelectField`). Fields top-align, so a hint or error under one does not
   * push the others down, and anything that is not a field (a button, a
   * chip, the `end` group) drops by one label line to sit level with the
   * inputs.
   */
  fields?: boolean;
  /** Names the group, e.g. «تصفية». */
  label?: string;
}

/** Search, filters and a count above a table. Wraps rather than overflowing. */
export function FilterBar({ children, end, fields = false, label }: FilterBarProps) {
  return (
    <div className="filter-bar" data-fields={fields || undefined} role={label === undefined ? undefined : 'group'} aria-label={label}>
      <div className="filter-bar-main">{children}</div>
      {end === undefined ? null : <div className="filter-bar-end">{end}</div>}
    </div>
  );
}

/**
 * One labelled filter inside `FilterBar fields`. A fixed width rather than a
 * flex-basis — the bar sizes from its children's max-content, and an input's
 * is narrower than any useful basis. `lg` for a select of long action names
 * or a free-text search.
 */
export function FilterField({ children, size = 'md' }: { children: ReactNode; size?: 'md' | 'lg' }) {
  return (
    <div className="filter-field" data-size={size}>
      {children}
    </div>
  );
}

/* ── SplitLayout ──────────────────────────────────────────────────── */

export interface SplitLayoutProps {
  /** The list to pick from (templates, groups). Narrow, at the inline start. */
  nav: ReactNode;
  /** The editor. */
  main: ReactNode;
  /** The live preview. Optional; when present it sits beside the editor on a wide screen and sticks. */
  aside?: ReactNode;
  /** Names each region for assistive technology, in order: nav, main, aside. */
  labels?: { nav?: string; aside?: string };
}

/**
 * List · editor · preview. Three columns from 1200px (the preview sticks, so
 * it answers each keystroke without scrolling); list and editor with the
 * preview beneath from 900px; one column below. Put a `Card` in each slot.
 */
export function SplitLayout({ nav, main, aside, labels }: SplitLayoutProps) {
  return (
    <div className="split" data-aside={aside === undefined ? undefined : true}>
      <div className="split-nav" role={labels?.nav === undefined ? undefined : 'region'} aria-label={labels?.nav}>
        {nav}
      </div>
      <div className="split-main">{main}</div>
      {aside === undefined ? null : (
        <div className="split-aside" role={labels?.aside === undefined ? undefined : 'region'} aria-label={labels?.aside}>
          {aside}
        </div>
      )}
    </div>
  );
}

/* ── AuthLayout ───────────────────────────────────────────────────── */

export interface AuthLayoutProps {
  /** The logo, on the brand panel. */
  brand: ReactNode;
  /** The promise, e.g. "لوحة المنصة — إدارة المنشآت لا محتواها". */
  headline: ReactNode;
  /** One or two sentences under it. */
  lead?: ReactNode;
  /** Short facts — three at most — listed on the brand panel. */
  points?: { icon?: ReactNode; text: ReactNode }[];
  /** The small line at the foot of the brand panel. */
  footnote?: ReactNode;
  /** The form side. */
  children: ReactNode;
  /** Theme toggle or locale, at the top of the form side. */
  corner?: ReactNode;
  /** `viewport` (default) fills the screen; `container` fills its parent — for previews. */
  fill?: 'viewport' | 'container';
}

/**
 * The split sign-in composition: brand panel on the inline start, form on
 * the inline end. Below 900px the brand panel shrinks to a header band and
 * the form takes the screen.
 */
export function AuthLayout({ brand, headline, lead, points, footnote, children, corner, fill = 'viewport' }: AuthLayoutProps) {
  return (
    <div className="auth" data-fill={fill}>
      <aside className="auth-brand">
        <div className="auth-brand-mark">{brand}</div>
        <div className="auth-brand-copy">
          <p className="auth-headline">{headline}</p>
          {lead === undefined ? null : <p className="auth-lead">{lead}</p>}
          {points === undefined ? null : (
            <ul className="auth-points">
              {points.map((point, index) => (
                <li key={index}>
                  {point.icon === undefined ? null : <span className="auth-point-icon">{point.icon}</span>}
                  <span>{point.text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {footnote === undefined ? null : <p className="auth-footnote">{footnote}</p>}
      </aside>
      {/* `main` only when it is the page; a preview inside a page must not add a second landmark. */}
      <div className="auth-main" id={fill === 'viewport' ? 'main' : undefined} role={fill === 'viewport' ? 'main' : undefined}>
        {corner === undefined ? null : <div className="auth-corner">{corner}</div>}
        <div className="auth-form">{children}</div>
      </div>
    </div>
  );
}
